import React from "react";
import { useDependencyInjector } from "@whatslly/commons/di";
import type { IUserStore } from "@whatslly/ui-components/services/UserStore";
import { InjectUserStore } from "@whatslly/ui-components/services/UserStore";
import { observer } from "mobx-react-lite";
import { UserPermissions } from "@whatslly/commons/granularPermissions";
import { hasPermission } from "@whatslly/commons/platformPermissions";
import styles from "./AuthorizedAction.module.scss";
import { useSearchParams } from "react-router-dom";

type PermissionProps =
  | { permission: UserPermissions; optionalPermissions?: never }
  | { permission?: never; optionalPermissions: UserPermissions[] }
  | { permission?: never; optionalPermissions?: never };

export type AuthorizedActionProps = {
  children: React.ReactNode;
  mode?: "hide" | "disable";
  adminOnly?: boolean;
  testId?: string;
} & PermissionProps;

export const AuthorizedAction: React.FC<AuthorizedActionProps> = observer(
  ({ children, permission, optionalPermissions, mode = "disable", adminOnly = false, testId }) => {
    const userStore = useDependencyInjector<IUserStore>(InjectUserStore);
    const [searchParams] = useSearchParams();
    const currentOrgId = searchParams.get("orgId") || userStore.user?.organizationId;
    const isAdmin = userStore.isAdmin || userStore.isSuperAdmin;
    const isSameOrg = Boolean(userStore.user && currentOrgId === userStore.user.organizationId);
    const hasUpdateAccessToOrg = userStore.hasUpdateAccessToOrg ?? false;
    const checkPermission = (): boolean => {
      if (permission) {
        return hasPermission(userStore.user, permission) && (isSameOrg || hasUpdateAccessToOrg);
      }
      if (optionalPermissions) {
        return Boolean(userStore.user && optionalPermissions.some((p) => hasPermission(userStore.user, p)) && (isSameOrg || hasUpdateAccessToOrg));
      }
      return isSameOrg || hasUpdateAccessToOrg;
    };

    const isAuthorized = isAdmin || (!adminOnly && checkPermission());
    const defaultTestId = "authorized-action-wrapper";
    const wrapperTestId = testId ?? defaultTestId;

    if (mode === "hide" && !isAuthorized) return <div className={styles.hidden} data-testid={wrapperTestId} />;
    if (mode === "disable" && !isAuthorized)
      return (
        <div className={styles.disabled} data-testid={wrapperTestId}>
          {children}
        </div>
      );
    return <div data-testid={wrapperTestId}>{children}</div>;
  }
);
